import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workersData, mockEvaluations } from '../../mocks/workers';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';

const RATING_CATEGORIES = [
  { key: "顧客目線_rating", label: "顧客目線" },
  { key: "笑顔_rating", label: "笑顔" },
  { key: "園の方針_rating", label: "園の方針" },
  { key: "事前準備_rating", label: "事前準備" },
  { key: "雰囲気_rating", label: "雰囲気" },
  { key: "運資質_rating", label: "運・資質" },
  { key: "清潔感_rating", label: "清潔感" },
  { key: "態度_rating", label: "態度" },
] as const;

// モックデータからWorker型に変換
interface Worker {
  id: string;
  name: string;
  email: string;
  qualifications: string[] | null;
  created_at: string;
  averageScore: number;
  rank: string;
  evaluationHistory: {
    evaluatedAt: string;
    punctuality: number;
    teamwork: number;
    childCare: number;
    communication: number;
  }[];
}

const getRankColor = (rank: string) => {
  switch (rank) {
    case 'S': return 'text-amber-600 bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-200';
    case 'A': return 'text-green-600 bg-gradient-to-br from-green-50 to-emerald-100 border-green-200';
    case 'B': return 'text-sky-600 bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200';
    case '未評価': return 'text-gray-500 bg-gray-50 border-gray-200';
    case 'ブロック': return 'text-gray-600 bg-gray-100 border-gray-200';
    default: return 'text-gray-600 bg-gray-100 border-gray-200';
  }
};

const getRankIcon = (rank: string) => {
  switch (rank) {
    case 'S': return 'ri-vip-crown-fill';
    case 'A': return 'ri-checkbox-circle-fill';
    case 'B': return 'ri-time-line';
    case '未評価': return 'ri-question-line';
    case 'ブロック': return 'ri-close-circle-fill';
    default: return 'ri-user-line';
  }
};

const getQualificationStyle = (qual: string) => {
  if (qual.includes('保育士')) return { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-user-heart-line' };
  if (qual.includes('看護師')) return { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'ri-stethoscope-line' };
  return { bg: 'bg-teal-100', text: 'text-teal-700', icon: 'ri-award-line' };
};

const getStrokeColor = (rank: string) => {
  if (rank === 'S') return '#f59e0b';
  if (rank === 'A') return '#22c55e';
  if (rank === 'B') return '#0ea5e9';
  return '#9ca3af';
};

const formatDate = (dateString: string) => {
  const d = new Date(dateString);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

// ---- RadarPreview ----
interface RadarPreviewProps {
  workerId: string;
  workerName: string;
  averageScore: number;
  rank: string;
  anchorRef: React.RefObject<HTMLElement>;
}

function RadarPreview({ workerId, workerName, averageScore, rank, anchorRef }: RadarPreviewProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const previewWidth = 300;
    const previewHeight = 300;
    const margin = 8;
    let left = rect.right + margin;
    let top = rect.top + rect.height / 2 - previewHeight / 2;
    if (left + previewWidth > window.innerWidth - 16) left = rect.left - previewWidth - margin;
    if (top < 8) top = 8;
    if (top + previewHeight > window.innerHeight - 8) top = window.innerHeight - previewHeight - 8;
    setPos({ top, left });
  }, [anchorRef]);

  const evals = mockEvaluations.filter(e => e.worker_id === workerId);
  if (evals.length === 0 || !pos) return null;

  const radarData = RATING_CATEGORIES.map(({ key, label }) => {
    const avg = evals.reduce((sum, ev) => sum + ((ev as any)[key] || 3), 0) / evals.length;
    return {
      category: label.length > 3 ? label.slice(0, 3) : label,
      fullLabel: label,
      value: avg * 2,
      maxValue: 10,
    };
  });

  return (
    <div
      className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-none"
      style={{ top: pos.top, left: pos.left, width: 300 }}
    >
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full">
            <span className="text-teal-700 font-bold text-sm">{workerName.charAt(0)}</span>
          </div>
          <span className="text-sm font-semibold text-gray-800">{workerName}</span>
        </div>
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border text-sm font-bold ${getRankColor(rank)}`}>
          {rank}ランク
        </div>
      </div>
      <div className="px-2 pt-1 pb-0">
        <ResponsiveContainer width="100%" height={200}>
          <RadarChart data={radarData} margin={{ top: 12, right: 20, bottom: 12, left: 20 }}>
            <PolarGrid stroke="#e5e7eb" gridType="polygon" />
            <PolarAngleAxis dataKey="category" tick={{ fill: "#6b7280", fontSize: 10 }} />
            <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} tickCount={6} axisLine={false} />
            <Tooltip
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg shadow px-2 py-1 pointer-events-none">
                      <p className="text-sm font-medium text-gray-800">{d.fullLabel}</p>
                      <p className="text-base font-bold" style={{ color: getStrokeColor(rank) }}>{(d.value as number).toFixed(1)}点</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Radar name="最大値" dataKey="maxValue" stroke="#e5e7eb" fill="#f3f4f6" fillOpacity={0.4} strokeWidth={1} strokeDasharray="4 2" />
            <Radar name="スコア" dataKey="value" stroke={getStrokeColor(rank)} fill={getStrokeColor(rank)} fillOpacity={0.25} strokeWidth={2} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-sm text-gray-500">評価 {evals.length}件の平均</span>
        <span className="text-base font-bold text-gray-800">{averageScore.toFixed(1)}点 <span className="text-sm font-normal text-gray-400">/ 80点</span></span>
      </div>
    </div>
  );
}

// ---- DetailButtonWithPreview ----
function DetailButtonWithPreview({ worker }: { worker: Worker }) {
  const [hovered, setHovered] = useState(false);
  const btnRef = useRef<HTMLAnchorElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div className="relative inline-block">
      <Link
        ref={btnRef as React.RefObject<HTMLAnchorElement>}
        to={`/mock-workers/${worker.id}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <i className="ri-eye-line"></i>
        詳細
      </Link>
      {hovered && !isMobile && (
        <RadarPreview
          workerId={worker.id}
          workerName={worker.name}
          averageScore={worker.averageScore}
          rank={worker.rank}
          anchorRef={btnRef as React.RefObject<HTMLElement>}
        />
      )}
    </div>
  );
}

// ---- TrendLinePreview ----
interface TrendLinePreviewProps {
  worker: Worker;
  type: 'growth' | 'decline';
  anchorRef: React.RefObject<HTMLElement>;
}

function TrendLinePreview({ worker, type, anchorRef }: TrendLinePreviewProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [animatedData, setAnimatedData] = useState<{ date: string; score: number; displayScore: number }[]>([]);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const previewWidth = 260;
    const previewHeight = 200;
    const margin = 10;
    let left = rect.right + margin;
    let top = rect.top + rect.height / 2 - previewHeight / 2;
    if (left + previewWidth > window.innerWidth - 16) left = rect.left - previewWidth - margin;
    if (top < 8) top = 8;
    if (top + previewHeight > window.innerHeight - 8) top = window.innerHeight - previewHeight - 8;
    setPos({ top, left });
  }, [anchorRef]);

  const fullData = [...(worker.evaluationHistory || [])]
    .sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime())
    .map(h => {
      const score = (h.punctuality + h.teamwork + h.childCare + h.communication) * 2;
      const d = new Date(h.evaluatedAt);
      return { date: `${d.getMonth() + 1}/${d.getDate()}`, score };
    });

  useEffect(() => {
    if (fullData.length === 0) return;
    setAnimatedData([]);
    let idx = 0;
    const step = () => {
      idx++;
      setAnimatedData(fullData.slice(0, idx).map(d => ({ ...d, displayScore: d.score })));
      if (idx < fullData.length) animFrameRef.current = window.setTimeout(step, 120);
    };
    animFrameRef.current = window.setTimeout(step, 60);
    return () => { if (animFrameRef.current) clearTimeout(animFrameRef.current); };
  }, [worker.id]);

  if (!pos || fullData.length < 2) return null;

  const isGrowth = type === 'growth';
  const color = isGrowth ? '#22c55e' : '#ef4444';
  const bgFrom = isGrowth ? 'from-green-50' : 'from-red-50';
  const borderColor = isGrowth ? 'border-green-200' : 'border-red-200';
  const labelColor = isGrowth ? 'text-green-700' : 'text-red-700';
  const labelBg = isGrowth ? 'bg-green-100' : 'bg-red-100';
  const icon = isGrowth ? 'ri-arrow-up-line' : 'ri-arrow-down-line';
  const label = isGrowth ? '成長中' : '下降中';
  const scores = fullData.map(d => d.score);
  const minScore = Math.max(0, Math.min(...scores) - 4);
  const maxScore = Math.min(40, Math.max(...scores) + 4);
  const firstScore = fullData[0].score;
  const lastScore = fullData[fullData.length - 1].score;
  const diff = lastScore - firstScore;
  const diffLabel = diff >= 0 ? `+${diff}` : `${diff}`;

  return (
    <div
      className={`fixed z-[9999] bg-gradient-to-b ${bgFrom} to-white rounded-xl shadow-2xl border ${borderColor} overflow-hidden pointer-events-none`}
      style={{ top: pos.top, left: pos.left, width: 260 }}
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">{worker.name}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${labelBg} ${labelColor}`}>
            <i className={`${icon} text-xs`}></i>{label}
          </span>
        </div>
        <span className={`text-sm font-bold ${isGrowth ? 'text-green-600' : 'text-red-600'}`}>{diffLabel}pt</span>
      </div>
      <div className="px-2 pb-1">
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={animatedData} margin={{ top: 8, right: 12, bottom: 4, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 9 }} axisLine={false} tickLine={false} />
            <YAxis domain={[minScore, maxScore]} tick={{ fill: '#9ca3af', fontSize: 9 }} axisLine={false} tickLine={false} width={28} />
            <ReferenceLine y={firstScore} stroke={color} strokeDasharray="4 3" strokeOpacity={0.35} strokeWidth={1} />
            <Line type="monotone" dataKey="displayScore" stroke={color} strokeWidth={2.5} dot={{ r: 4, fill: color, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 5, fill: color }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">{fullData[0].date} → {fullData[fullData.length - 1].date}</span>
        <span className="text-xs text-gray-500">{fullData.length}回の評価</span>
      </div>
    </div>
  );
}

// ---- TrendBadge ----
function TrendBadge({ worker, type }: { worker: Worker; type: 'growth' | 'decline' }) {
  const [hovered, setHovered] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const isGrowth = type === 'growth';
  const color = isGrowth
    ? { ping: 'bg-green-400', dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-100', icon: 'ri-arrow-up-line', label: '成長中' }
    : { ping: 'bg-red-400', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-100', icon: 'ri-arrow-down-line', label: '下降中' };

  return (
    <div
      className="relative inline-flex items-center gap-1 cursor-default"
      ref={badgeRef}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative">
        <div className={`absolute inset-0 ${color.ping} rounded-full animate-ping opacity-75`}></div>
        <div className={`relative w-4 h-4 flex items-center justify-center ${color.dot} rounded-full`}>
          <i className={`${color.icon} text-white text-xs animate-bounce`}></i>
        </div>
      </div>
      <span className={`text-xs ${color.bg} ${color.text} px-2 py-0.5 rounded-full font-medium animate-pulse`}>{color.label}</span>
      {hovered && !isMobile && (
        <TrendLinePreview worker={worker} type={type} anchorRef={badgeRef as React.RefObject<HTMLElement>} />
      )}
    </div>
  );
}

// ---- トレンド判定 ----
function isGrowthTrend(worker: Worker) {
  if (!worker.evaluationHistory || worker.evaluationHistory.length < 2) return false;
  const history = [...worker.evaluationHistory].sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime());
  if (history.length < 3) {
    const first = (history[0].punctuality + history[0].teamwork + history[0].childCare + history[0].communication) * 2;
    const last = (history[history.length - 1].punctuality + history[history.length - 1].teamwork + history[history.length - 1].childCare + history[history.length - 1].communication) * 2;
    return last > first + 4;
  }
  let cnt = 0;
  for (let i = 1; i < history.length; i++) {
    const prev = (history[i-1].punctuality + history[i-1].teamwork + history[i-1].childCare + history[i-1].communication) * 2;
    const cur = (history[i].punctuality + history[i].teamwork + history[i].childCare + history[i].communication) * 2;
    if (cur > prev) cnt++;
  }
  return cnt >= Math.ceil((history.length - 1) / 2);
}

function isDeclineTrend(worker: Worker) {
  if (!worker.evaluationHistory || worker.evaluationHistory.length < 2) return false;
  const history = [...worker.evaluationHistory].sort((a, b) => new Date(a.evaluatedAt).getTime() - new Date(b.evaluatedAt).getTime());
  if (history.length < 3) {
    const first = (history[0].punctuality + history[0].teamwork + history[0].childCare + history[0].communication) * 2;
    const last = (history[history.length - 1].punctuality + history[history.length - 1].teamwork + history[history.length - 1].childCare + history[history.length - 1].communication) * 2;
    return first > last + 4;
  }
  let cnt = 0;
  for (let i = 1; i < history.length; i++) {
    const prev = (history[i-1].punctuality + history[i-1].teamwork + history[i-1].childCare + history[i-1].communication) * 2;
    const cur = (history[i].punctuality + history[i].teamwork + history[i].childCare + history[i].communication) * 2;
    if (cur < prev) cnt++;
  }
  return cnt >= Math.ceil((history.length - 1) / 2);
}

// ---- メインページ ----
export default function MockWorkersPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [qualFilter, setQualFilter] = useState('');
  const [rankFilter, setRankFilter] = useState('');

  // workersDataをWorker型に変換
  const workers: Worker[] = workersData.map(w => ({
    id: w.id,
    name: w.name,
    email: w.email,
    qualifications: w.qualifications,
    created_at: w.created_at,
    averageScore: w.averageScore,
    rank: w.rank,
    evaluationHistory: w.evaluationHistory.map(h => ({
      evaluatedAt: h.evaluatedAt,
      punctuality: h.punctuality,
      teamwork: h.teamwork,
      childCare: h.childCare,
      communication: h.communication,
    })),
  }));

  const filtered = workers.filter(w => {
    const matchSearch = w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchQual = !qualFilter || (w.qualifications && w.qualifications.includes(qualFilter));
    const matchRank = !rankFilter || w.rank === rankFilter;
    return matchSearch && matchQual && matchRank;
  });

  const totalWorkers = workers.length;
  const nurseryCount = workers.filter(w => w.qualifications?.some(q => q.includes('保育士'))).length;
  const nurseCount = workers.filter(w => w.qualifications?.some(q => q.includes('看護師'))).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-teal-100 rounded-lg">
                <i className="ri-database-2-line text-teal-600 text-xl sm:text-2xl"></i>
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">モックワーカー一覧</h1>
                <p className="text-sm text-gray-500 hidden sm:block">テスト用データプレビュー</p>
              </div>
              {/* 統計バッジ */}
              <div className="hidden sm:flex items-center gap-2 ml-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full">
                  <i className="ri-team-line text-teal-600 text-sm"></i>
                  <span className="text-sm text-teal-600 font-medium whitespace-nowrap">総ワーカー数</span>
                  <span className="text-base font-bold text-teal-700">{totalWorkers}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                  <i className="ri-user-heart-line text-green-600 text-sm"></i>
                  <span className="text-sm text-green-600 font-medium whitespace-nowrap">保育士</span>
                  <span className="text-base font-bold text-green-700">{nurseryCount}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-full">
                  <i className="ri-stethoscope-line text-sky-600 text-sm"></i>
                  <span className="text-sm text-sky-600 font-medium whitespace-nowrap">看護師</span>
                  <span className="text-base font-bold text-sky-700">{nurseCount}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/admin/workers"
                className="flex items-center justify-center gap-2 px-3 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shield-user-line"></i>
                <span className="hidden sm:inline">管理画面へ</span>
              </Link>
              <Link
                to="/"
                className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-2 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-lg"></i>
                <span className="text-sm hidden sm:inline">ホーム</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* 注意バナー */}
        <div className="mb-5 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <i className="ri-information-line text-amber-500 text-xl flex-shrink-0"></i>
          <p className="text-sm text-amber-800">
            このページはモック（テスト用）データのプレビューです。実際のSupabaseデータとは異なります。
          </p>
        </div>

        {/* 検索・フィルター */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="名前・メールで検索"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <select
              value={qualFilter}
              onChange={e => setQualFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="">全ての資格</option>
              <option value="保育士">保育士</option>
              <option value="看護師">看護師</option>
              <option value="栄養士">栄養士</option>
            </select>
            <select
              value={rankFilter}
              onChange={e => setRankFilter(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="">全てのランク</option>
              <option value="S">Sランク</option>
              <option value="A">Aランク</option>
              <option value="B">Bランク</option>
              <option value="ブロック">ブロック</option>
            </select>
            <button
              onClick={() => { setSearchTerm(''); setQualFilter(''); setRankFilter(''); }}
              className="w-full px-4 py-2.5 bg-gray-100 text-gray-600 font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap text-sm"
            >
              <i className="ri-refresh-line"></i>
              リセット
            </button>
          </div>
        </div>

        {/* ワーカー一覧テーブル */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">ワーカー一覧</h3>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-user-search-line text-gray-400 text-2xl sm:text-3xl"></i>
              </div>
              <p className="text-gray-500 mb-2 text-base sm:text-lg">該当するワーカーが見つかりません</p>
              <p className="text-gray-400 text-sm">検索条件を変更してお試しください</p>
            </div>
          ) : (
            <>
              {/* モバイル用カード */}
              <div className="sm:hidden divide-y divide-gray-200">
                {filtered.map(worker => (
                  <div key={worker.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                        <span className="text-teal-700 font-bold text-lg">{worker.name.charAt(0)}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-gray-900 truncate text-base">{worker.name}</h4>
                          {isGrowthTrend(worker) && <TrendBadge worker={worker} type="growth" />}
                          {isDeclineTrend(worker) && <TrendBadge worker={worker} type="decline" />}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{worker.email}</p>
                      </div>
                      <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${getRankColor(worker.rank)}`}>
                        <i className={`${getRankIcon(worker.rank)} text-sm`}></i>
                        <span className="font-bold text-sm">{worker.rank}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {worker.qualifications?.map((qual, idx) => {
                        const style = getQualificationStyle(qual);
                        return (
                          <span key={idx} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                            <i className={`${style.icon} text-xs`}></i>{qual}
                          </span>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span>評価数: {worker.evaluationHistory.length}件</span>
                      <span>平均: {worker.averageScore.toFixed(1)}点</span>
                      <span>{formatDate(worker.created_at)}</span>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        to={`/mock-workers/${worker.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-eye-line"></i>詳細
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* デスクトップ用テーブル */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">ワーカー</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">資格</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">ランク</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">評価</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">登録日</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">アクション</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filtered.map(worker => (
                      <tr key={worker.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="w-11 h-11 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                              <span className="text-teal-700 font-bold text-base">{worker.name.charAt(0)}</span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <p className="text-base font-semibold text-gray-900">{worker.name}</p>
                                {isGrowthTrend(worker) && <TrendBadge worker={worker} type="growth" />}
                                {isDeclineTrend(worker) && <TrendBadge worker={worker} type="decline" />}
                              </div>
                              <p className="text-sm text-gray-500 truncate">{worker.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {worker.qualifications && worker.qualifications.length > 0 ? (
                              worker.qualifications.map((qual, idx) => {
                                const style = getQualificationStyle(qual);
                                return (
                                  <span key={idx} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                                    <i className={`${style.icon} text-xs`}></i>{qual}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-sm text-gray-400">なし</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getRankColor(worker.rank)}`}>
                            <i className={`${getRankIcon(worker.rank)}`}></i>
                            <span className="font-bold text-sm">{worker.rank}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="text-sm">
                            <p className="font-semibold text-gray-900 text-base">{worker.averageScore.toFixed(1)}点</p>
                            <p className="text-sm text-gray-500">{worker.evaluationHistory.length}件</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-600">
                          {formatDate(worker.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-start gap-2">
                            <DetailButtonWithPreview worker={worker} />
                            {worker.rank !== 'B' && worker.rank !== '未評価' && (
                              <Link
                                to={`/mock-workers/${worker.id}/offer`}
                                className={`inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white shadow-sm ${
                                  worker.rank === 'ブロック'
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                                    : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700'
                                } focus:outline-none whitespace-nowrap cursor-pointer`}
                              >
                                <i className={`ri-${worker.rank === 'ブロック' ? 'notification-2-line' : 'gift-line'} mr-1`}></i>
                                {worker.rank === 'ブロック' ? '通知' : 'オファー'}
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
